import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import StripePaymentForm from '../components/StripePaymentForm';

const ALLOWED_ROLES = new Set(['admin', 'app_manager', 'org_admin', 'coach']);

const StripeSettings = () => {
  const { user } = useAuth();
  const isAppManager = user?.role === 'app_manager';
  const isAllowed = isAppManager && ALLOWED_ROLES.has(user?.role);
  const canManageOrganization = isAllowed && ['admin', 'app_manager', 'org_admin'].includes(user?.role);
  const canManageTeam = isAllowed && ['admin', 'app_manager', 'coach'].includes(user?.role);

  const initialScope = useMemo(() => {
    if (!isAllowed) return 'user';
    if (canManageTeam) return 'team';
    if (canManageOrganization) return 'organization';
    return 'user';
  }, [isAllowed, canManageTeam, canManageOrganization]);

  const [scopeType, setScopeType] = useState(initialScope);
  const [organizationIdInput, setOrganizationIdInput] = useState('');
  const [availableTeams, setAvailableTeams] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState(user?.team_id ? String(user.team_id) : '');
  const [accountStatus, setAccountStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [onboardingLoading, setOnboardingLoading] = useState(false);

  const [testAmount, setTestAmount] = useState('20.00');
  const [testDescription, setTestDescription] = useState('');
  const [isCreatingPayment, setIsCreatingPayment] = useState(false);
  const [testPaymentConfig, setTestPaymentConfig] = useState(null);
  const [testPaymentStatus, setTestPaymentStatus] = useState(null);

  useEffect(() => {
    if (!isAllowed) {
      setScopeType('user');
    }
  }, [isAllowed]);

  useEffect(() => {
    if (!canManageTeam) return;
    const loadTeams = async () => {
      try {
        const response = await axios.get('/api/teams/my-teams');
        const teams = response.data || [];
        setAvailableTeams(teams);
        if (!selectedTeamId && teams.length > 0) {
          setSelectedTeamId(String(teams[0].id));
        }
      } catch (error) {
        console.error('Failed to load teams:', error);
        setAvailableTeams([]);
      }
    };
    loadTeams();
  }, [canManageTeam, selectedTeamId]);

  const resolveScopePayload = () => {
    const payload = { scopeType };
    if (scopeType === 'organization') {
      if (!organizationIdInput) {
        return null;
      }
      payload.scopeId = Number(organizationIdInput);
    } else if (scopeType === 'team') {
      const teamScopeId = selectedTeamId || (user?.team_id ? String(user.team_id) : '');
      if (!teamScopeId) {
        return null;
      }
      payload.scopeId = Number(teamScopeId);
    }
    return payload;
  };

  const fetchAccountStatus = async () => {
    if (!isAllowed) {
      setAccountStatus(null);
      return;
    }

    try {
      setStatusLoading(true);
      const params = new URLSearchParams();
      params.set('scopeType', scopeType);

      if (scopeType === 'organization') {
        if (!organizationIdInput) {
          setAccountStatus(null);
          return;
        }
        params.set('scopeId', organizationIdInput);
      } else if (scopeType === 'team') {
        const teamScopeId = selectedTeamId || (user?.team_id ? String(user.team_id) : '');
        if (!teamScopeId) {
          setAccountStatus(null);
          return;
        }
        params.set('scopeId', teamScopeId);
      }

      const response = await axios.get(`/api/stripe/accounts?${params.toString()}`);
      setAccountStatus(response.data || null);
    } catch (error) {
      console.error('Failed to fetch Stripe account status:', error);
      setAccountStatus(null);
    } finally {
      setStatusLoading(false);
    }
  };

  useEffect(() => {
    fetchAccountStatus();
    setTestPaymentConfig(null);
    setTestPaymentStatus(null);
  }, [scopeType, organizationIdInput, selectedTeamId]);

  // Show access denied if not app_manager - must be after ALL hooks
  if (!isAppManager) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h2>
            <p className="text-gray-600 mb-4">
              Stripe Settings is only available to App Managers.
            </p>
            <p className="text-sm text-gray-500">
              Coaches and organization admins can manage their Stripe Connect accounts through their Profile page.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const handleConnectStripe = async () => {
    const payload = resolveScopePayload();
    if (scopeType !== 'user' && !payload) {
      toast.error('Provide all required scope details before connecting Stripe.');
      return;
    }

    try {
      setOnboardingLoading(true);
      const response = await axios.post('/api/stripe/accounts', payload || { scopeType: 'user' });
      if (response.data?.onboardingUrl) {
        window.location.href = response.data.onboardingUrl;
      } else {
        toast.error('Unable to generate Stripe onboarding link.');
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to initiate Stripe onboarding.';
      toast.error(message);
    } finally {
      setOnboardingLoading(false);
    }
  };

  const handleCreateTestPayment = async () => {
    if (!accountStatus?.connected) {
      toast.error('Connect Stripe before running a test payment.');
      return;
    }

    const payload = resolveScopePayload();
    if (scopeType !== 'user' && !payload) {
      toast.error('Provide scope details before running a test payment.');
      return;
    }

    const parsedAmount = Number.parseFloat(testAmount);
    if (Number.isNaN(parsedAmount) || parsedAmount <= 0) {
      toast.error('Enter a valid amount (e.g., 20.00).');
      return;
    }

    const amountInCents = Math.round(parsedAmount * 100);
    if (amountInCents < 50) {
      toast.error('Amount must be at least $0.50.');
      return;
    }

    try {
      setIsCreatingPayment(true);
      setTestPaymentStatus(null);
      setTestPaymentConfig(null);

      const response = await axios.post('/api/stripe/test-payment', {
        ...(payload || { scopeType: 'user' }),
        amount: amountInCents,
        currency: 'usd',
        description: testDescription
      });

      setTestPaymentConfig({
        clientSecret: response.data.clientSecret,
        publishableKey: response.data.publishableKey,
        amount: response.data.amount,
        currency: response.data.currency || 'usd'
      });

      toast.success('Test PaymentIntent created. Complete the form below.');
    } catch (error) {
      const message =
        error.response?.data?.message || 'Failed to create test payment intent.';
      toast.error(message);
    } finally {
      setIsCreatingPayment(false);
    }
  };

  const handleTestPaymentComplete = (result) => {
    if (result?.success) {
      setTestPaymentStatus({
        success: true,
        message: `Payment ${result.paymentIntent?.status || 'succeeded'}`
      });
      toast.success('Test payment completed!');
    } else if (result?.error) {
      setTestPaymentStatus({ success: false, message: result.error });
      toast.error(result.error || 'Test payment failed.');
    }
  };

  if (!isAllowed) {
    return (
      <div className="max-w-4xl mx-auto py-10 px-4">
        <div className="bg-white shadow rounded-lg p-8">
          <h1 className="text-2xl font-bold text-gray-900">Stripe Settings</h1>
          <p className="mt-4 text-gray-600">
            You do not have permission to manage Stripe settings. Please contact an administrator if you believe this is an error.
          </p>
        </div>
      </div>
    );
  }

  const chargesEnabled = accountStatus?.account?.chargesEnabled;
  const payoutsEnabled = accountStatus?.account?.payoutsEnabled;
  const detailsSubmitted = accountStatus?.account?.detailsSubmitted;

  return (
    <div className="max-w-5xl mx-auto py-10 px-4">
      <div className="bg-white shadow rounded-lg p-6">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Stripe Payments</h1>
          <p className="mt-2 text-gray-600">
            Connect your Stripe account with a single click using Stripe Connect. FieldHouse keeps your API keys secure and routes payments to the right destination.
          </p>
        </div>

        {/* Scope Selection */}
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900">Connection Scope</h2>
          <p className="text-sm text-gray-600 mb-3">
            Choose whether this Stripe connection applies to you personally, your team, or your organization.
          </p>

          <div className="flex flex-wrap gap-4">
            <label className="inline-flex items-center">
              <input
                type="radio"
                name="scopeType"
                value="user"
                checked={scopeType === 'user'}
                onChange={() => setScopeType('user')}
                className="h-4 w-4 text-primary-600 border-gray-300"
              />
              <span className="ml-2 text-sm text-gray-700">User</span>
            </label>

            {canManageTeam && (
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  name="scopeType"
                  value="team"
                  checked={scopeType === 'team'}
                  onChange={() => setScopeType('team')}
                  className="h-4 w-4 text-primary-600 border-gray-300"
                />
                <span className="ml-2 text-sm text-gray-700">Team</span>
              </label>
            )}

            {canManageOrganization && (
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  name="scopeType"
                  value="organization"
                  checked={scopeType === 'organization'}
                  onChange={() => setScopeType('organization')}
                  className="h-4 w-4 text-primary-600 border-gray-300"
                />
                <span className="ml-2 text-sm text-gray-700">Organization</span>
              </label>
            )}
          </div>

          {scopeType === 'team' && (
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Team</label>
              <select
                value={selectedTeamId}
                onChange={(event) => setSelectedTeamId(event.target.value)}
                className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              >
                <option value="">Select a team...</option>
                {availableTeams.map((team) => (
                  <option key={team.id} value={team.id}>
                    {team.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {scopeType === 'organization' && (
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Organization ID
              </label>
              <input
                type="text"
                value={organizationIdInput}
                onChange={(event) => setOrganizationIdInput(event.target.value)}
                placeholder="Enter organization ID"
                className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              />
            </div>
          )}
        </div>

        {/* Status */}
        <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Connection Status
              </h2>
              <p className="text-sm text-gray-600">
                {statusLoading
                  ? 'Checking Stripe account...'
                  : accountStatus?.connected
                    ? 'Stripe account connected.'
                    : 'No Stripe account connected yet.'}
              </p>
            </div>
            <button
              onClick={fetchAccountStatus}
              disabled={statusLoading}
              className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              {statusLoading ? 'Refreshing...' : 'Refresh Status'}
            </button>
          </div>

          {accountStatus?.connected && (
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-700">
              <div>
                <span className="font-medium text-gray-900">Account ID:</span>{' '}
                {accountStatus.account?.accountId || 'N/A'}
              </div>
              <div>
                <span className="font-medium text-gray-900">Business Name:</span>{' '}
                {accountStatus.account?.businessName || 'Not provided'}
              </div>
              <div>
                <span className="font-medium text-gray-900">Email:</span>{' '}
                {accountStatus.account?.email || 'Not provided'}
              </div>
              <div>
                <span className="font-medium text-gray-900">Environment:</span>{' '}
                {accountStatus.environment || 'test'}
              </div>
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-900">Charges Enabled:</span>
                <span
                  className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${
                    chargesEnabled ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                  }`}
                >
                  {chargesEnabled ? 'Yes' : 'Pending'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-900">Payouts Enabled:</span>
                <span
                  className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${
                    payoutsEnabled ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                  }`}
                >
                  {payoutsEnabled ? 'Yes' : 'Pending'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-900">Details Submitted:</span>
                <span
                  className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${
                    detailsSubmitted ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                  }`}
                >
                  {detailsSubmitted ? 'Yes' : 'Incomplete'}
                </span>
              </div>
              <div className="text-xs text-gray-500 md:col-span-2">
                Last synced:{' '}
                {accountStatus.account?.lastSyncedAt
                  ? new Date(accountStatus.account.lastSyncedAt).toLocaleString()
                  : 'Not available'}
              </div>
            </div>
          )}

          <div className="mt-4 flex flex-wrap gap-3">
            <button
              onClick={handleConnectStripe}
              disabled={onboardingLoading}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-60"
            >
              {onboardingLoading ? 'Preparing...' : 'Connect or Update Stripe'}
            </button>
            {accountStatus?.connected && (
              <a
                href={accountStatus.account?.accountId
                  ? `https://dashboard.stripe.com/${accountStatus.environment === 'live' ? '' : 'test/'}connect/accounts/${accountStatus.account.accountId}`
                  : 'https://dashboard.stripe.com/'}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                Open Stripe Dashboard
              </a>
            )}
          </div>
        </div>

        {/* Test Payment */}
        <div className="mt-8 border border-gray-200 rounded-md p-4">
          <h2 className="text-lg font-semibold text-gray-900">Test Payment Flow</h2>
          <p className="text-sm text-gray-600 mb-4">
            Generate a test payment to confirm the Stripe connection. Use Stripe test cards to simulate different scenarios.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Amount (USD)</label>
              <input
                type="text"
                value={testAmount}
                onChange={(event) => setTestAmount(event.target.value)}
                placeholder="20.00"
                className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description (Optional)
              </label>
              <input
                type="text"
                value={testDescription}
                onChange={(event) => setTestDescription(event.target.value)}
                placeholder="Test payment description"
                className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              />
            </div>
          </div>

          <div className="mt-4">
            <button
              onClick={handleCreateTestPayment}
              disabled={isCreatingPayment}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-60"
            >
              {isCreatingPayment ? 'Preparing...' : 'Generate Test Payment Intent'}
            </button>
          </div>

          {testPaymentConfig && (
            <div className="mt-6">
              <div className="p-4 bg-green-50 border border-green-200 rounded">
                <p className="text-sm text-green-800">
                  Complete the secure Stripe checkout form below to finalize the test payment.
                </p>
              </div>
              <div className="mt-4">
                <StripePaymentForm
                  config={testPaymentConfig}
                  onComplete={handleTestPaymentComplete}
                />
              </div>
            </div>
          )}

          {testPaymentStatus && (
            <div
              className={`mt-4 border rounded-md p-4 ${
                testPaymentStatus.success ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'
              }`}
            >
              <p className="text-sm font-medium">
                {testPaymentStatus.success ? 'Test payment completed successfully.' : 'Test payment failed.'}
              </p>
              <p className="text-sm mt-1">
                {testPaymentStatus.message}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StripeSettings;


